/** @jsxImportSource @emotion/react */
import { useState, useMemo, SyntheticEvent } from 'react'
import styled from '@emotion/styled'
import moment from 'moment'
import { Document, Page, pdfjs } from 'react-pdf'
import Dropdown from 'react-bootstrap/Dropdown'
import BsButton from 'react-bootstrap/Button'

import { Order, UploadType } from 'store/types'
import { VerifyStatusButton } from './VerifyStatusButton'
import Button from 'components/atoms/Button'
import Link from 'components/atoms/Link'
import { TooltipWrapper } from './TooltipWrapper'
import { VerifyType } from './OrderEdit'
import {
  useAcceptLabRxCandidateMutation,
  useChangeLabFileCategoryMutation,
  useRecordLabDocumentDownloadMutation,
} from 'store/services/labOrder'
import { confirm } from 'components/molecules/Confirmation'
import useWindowSize from 'hooks/useWindowSize'
import { primaryDiagnosisCodes } from 'ts-common/constants/primaryDiagnosisCodes'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`

const StyledDropdownItem = styled(Dropdown.Item)`
  padding: 0.55rem 1rem 0.55rem 1.7rem;
`

const FilenameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  padding-left: 1rem;
`

const ListGroupItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 1rem;
  padding-right: 0.5rem;
`

const DocumentWrapper = styled.div`
  background-color: #ccc;
  margin-right: 1rem;
  max-height: 65vh;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  padding: 1rem;
  transition: all 0.5s ease;
`

const DocImage = styled.img`
  width: 100%;
`

const AudioPlayerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 8px;
  margin-left: 1rem;

  audio {
    width: 100%;
    max-width: 500px;
  }
`

const AudioFileName = styled.div`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #2c83bd;
  }
`

interface IProps {
  expanded: boolean
  order: Order
  uploadType: UploadType
  className?: string
  verification?: String
  setVerification: (v: string) => void
  removeFile: (fileKey: string) => void
  orderState?: any
  showVerifyButtons?: boolean
  rxUploaded: boolean
  onUploaded: boolean
  aobUploaded: boolean
}

const PREVIEW_MAX_PAGES = 20

const verifyableDocuments = (testType: string) =>
  testType === 'HST' || testType === 'PSG' ? ['RX', 'AOB', 'OFFICE_NOTES', 'PAPERWORK'] : ['RX', 'AOB']

const normalizeFaxNumber = (fax?: string | null) => String(fax || '').replace(/\D/g, '').replace(/^1/, '')

export const DocumentPanel = ({
  order,
  uploadType,
  className,
  verification,
  setVerification,
  removeFile,
  orderState,
  showVerifyButtons,
  rxUploaded,
  onUploaded,
  aobUploaded,
}: IProps) => {
  const files = useMemo(
    () => Object.values(order.files).filter((f) => f.uploadType === uploadType),
    [order],
  )
  const { height } = useWindowSize()
  const [numPages, setNumPages] = useState<{ [idx: number]: number }>({})
  const [expanded, setExpanded] = useState(files.map((f) => !!verification && verification !== 'verified'))
  const [zoom, setZoom] = useState<{ [idx: string]: number }>({})
  const [changeFileCategory] = useChangeLabFileCategoryMutation()
  const [acceptRxCandidate, { isLoading: acceptingRxCandidate }] = useAcceptLabRxCandidateMutation()
  const [recordDocumentDownload] = useRecordLabDocumentDownloadMutation()
  const onDocumentLoadSuccess = (idx: number) => ({ numPages: count }: { numPages: number }) => {
    setNumPages((p) => ({ ...p, [idx]: count }))
  }
  const primaryEligibleCodes = useMemo(
    () => (order?.testType ? primaryDiagnosisCodes?.[order?.testType] ?? [] : []),
    [order?.testType],
  )

  const primaryDiagnosisCodesEntered = !!order?.diagnosis?.filter((x) => primaryEligibleCodes?.includes(x))?.length
  const diagnosisCodesEntered = !!order?.diagnosis?.length
  const verifyableDocument = verifyableDocuments(order?.testType)?.includes(uploadType)
  const dmeFax = normalizeFaxNumber(order?.office?.fax)
  const physicianFax = normalizeFaxNumber(order?.physicianAddress?.fax)

  // useEffect(() => {
  //   if (verification === 'verified') {
  //     setExpanded(files.map((f) => verification !== 'verified'))
  //   }
  // }, [verification])

  const changeFileType = (category: string, fileKey: string) => () => {
    changeFileCategory({ orderId: Number(order.id), fileKey, toUploadType: category })
  }
  // The button sits in the row, so it asks first; the current RX becomes a
  // candidate and a verified RX goes back for verification.
  const takeAsRx = (fileKey: string) => (e: SyntheticEvent) => {
    e.stopPropagation()

    confirm('', {
      title: 'Use as RX',
      description: rxUploaded
        ? 'This document will be used as the RX, and the current RX will be marked as an RX candidate. If the current RX was verified, it will need to be verified again.'
        : 'This document will be used as the RX.',
      yesAction: 'Use as RX',
      noAction: 'Cancel',
    })
      .then(() => acceptRxCandidate({ orderId: Number(order.id), fileKey }))
      .catch(() => undefined)
  }
  const trackDocumentDownload = (fileKey: string) => () => {
    recordDocumentDownload({ orderId: Number(order.id), fileKey })
  }

  return files.map((file, idx) => {
    const ext = file?.filename.split('.').pop() ?? ''
    // Outsource (download-denied) users receive no file URL from the API, so we render neither a
    // preview nor a download link — the document is listed but not openable.
    const docUrl = file?.url
    const isExpanded = expanded[idx]
    const isMp3 = ext?.toLowerCase() === 'mp3'
    const isMovedReport = file?.uploadType === 'MOVED_REPORT'
    // The kebab menu holds the Download link plus the "Change Type"/Delete actions. Hide the whole
    // menu when neither is available (e.g. an outsource user on a verified or moved-report file).
    const canChangeType = verification !== 'verified' && !isMovedReport
    const hasMenuOptions = !!docUrl || canChangeType
    const faxedTo = normalizeFaxNumber(file?.faxedTo)
    const faxedLabel =
      faxedTo && faxedTo === dmeFax
        ? 'Faxed to DME'
        : faxedTo && faxedTo === physicianFax
          ? 'Faxed to physician'
          : 'Faxed'

    return (
      <div
        className={className}
        css={{ cursor: 'pointer' }}
        onClick={(e) => {
          if (['fas fa-ellipsis-v', 'btn', 'dropdown-item'].includes(e.target.className)) {
            return false
          }
          setExpanded((p) => [...p.slice(0, idx), !p[idx], ...p.slice(idx + 1)])
        }}>
        <div className="row" css={{ position: 'relative' }}>
          {isExpanded && !isMp3 && (
            <div
              css={{
                position: 'absolute',
                top: 58,
                right: 40,
                zIndex: 1,
                background: 'white',
                opacity: 0.8,
                padding: 5,
                borderRadius: 10,
              }}>
              <i
                className="fa fa-search-minus"
                css={{
                  color: '#2c83bd',
                  cursor: 'pointer',
                  fontSize: 30,
                  marginRight: 10,
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  if (zoom[idx] > 1) {
                    setZoom((p) => ({ ...p, [idx]: p[idx] - 0.1 }))
                  }
                }}
              />
              <i
                className="fa fa-search-plus"
                css={{
                  color: '#2c83bd',
                  cursor: 'pointer',
                  fontSize: 30,
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!zoom[idx] || zoom[idx] <= 3) {
                    setZoom((p) => ({ ...p, [idx]: (p[idx] || 1) + 0.1 }))
                  }
                }}
              />
            </div>
          )}
          <div className="col-sm-12">
            <div className="list-group text-secondary">
              <TooltipWrapper orderState={orderState}>
                <ListGroupItem className="row list-group-item list-group-item-action">
                  <i className={isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-up'} />
                  <div className="col-sm-2 text-gray">
                    {uploadType?.replace(/_/, ' ')}
                    {uploadType === 'RX' && order.officeNotesInRx && (
                      <span className="ml-1">/ Office Notes</span>
                    )}
                  </div>
                  <i className={`fa fa-fw fa-download`} style={{ color: '#4A89B3' }}></i>

                  <FilenameWrapper>
                    <Link to="#">{file?.filename}</Link>
                    <span style={{ fontSize: '0.8rem', opacity: 0.5, marginTop: 5 }}>
                      {moment(file?.uploadDate).format('lll')}
                    </span>
                  </FilenameWrapper>
                  {file?.faxedAt && (
                    <div
                      className="d-flex flex-column"
                      css={{
                        alignItems: 'flex-end',
                      }}>
                      <span
                        css={{
                          color: '#73B666',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                        {faxedLabel}
                      </span>
                      <span
                        css={{
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}>
                        {moment(file.faxedAt).format('HH:mm:ss MM/DD/YYYY')}
                      </span>
                    </div>
                  )}
                  {showVerifyButtons && verifyableDocument && (
                    <VerifyStatusButton
                      disabled={!orderState?.isUnderVerification}
                      value={verification}
                      // onChange={(type: VerifyType) => {
                      //   if (type === 'verified' && !primaryDiagnosisCodesEntered && uploadType === 'RX') {
                      //     try {
                      //       confirm('', {
                      //         title: 'Missing Primary Diagnosis Codes',
                      //         description:
                      //           'RX document cannot be verified until at least one primary diagnosis code is entered for the order.',
                      //         yesAction: `OK`,
                      //         noAction: null,
                      //       })
                      //     } catch {}
                      //   } else {
                      //     setVerification(type)
                      //   }
                      // }}
                      onChange={(type: VerifyType) => {
                        if (type === 'verified' && !diagnosisCodesEntered && uploadType === 'RX') {
                          try {
                            confirm('', {
                              title: 'Missing Diagnosis Codes',
                              description:
                                'RX document cannot be verified until diagnosis codes have been entered for the order.',
                              yesAction: `OK`,
                              noAction: null,
                            })
                          } catch {}
                        } else {
                          setVerification(type)
                        }
                      }}
                    />
                  )}

                  {uploadType === 'RX_CANDIDATE' && canChangeType && (
                    <BsButton
                      size="sm"
                      variant="outline-primary"
                      className="ml-2"
                      disabled={acceptingRxCandidate}
                      onClick={takeAsRx(file.key)}>
                      Use as RX
                    </BsButton>
                  )}

                  {hasMenuOptions && (
                  <div className="dropdown ml-2">
                    <button
                      className="btn"
                      type="button"
                      id="dropdownMenuButton"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false">
                      <i className="fas fa-ellipsis-v"></i>
                    </button>
                    <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                      {docUrl && (
                        <a className="dropdown-item" href={docUrl} target="_blank" rel="noreferrer" onClick={trackDocumentDownload(file.key)}>
                          Download
                        </a>
                      )}
                      {canChangeType && (
                        <>
                          <Dropdown.Header css={{ textAlign: 'start' }}>
                            Change Type:
                          </Dropdown.Header>
                          {uploadType !== 'RX_CANDIDATE' && (
                            <StyledDropdownItem
                              disabled={rxUploaded}
                              onClick={changeFileType('RX', file.key)}>
                              RX {rxUploaded && <span className="text-muted">(Uploaded)</span>}
                            </StyledDropdownItem>
                          )}
                          {order?.testType === 'HST' && (
                            <StyledDropdownItem
                              disabled={onUploaded}
                              onClick={changeFileType('OFFICE_NOTES', file.key)}>
                              Office Notes {onUploaded && <span className="text-muted">(Uploaded)</span>}
                            </StyledDropdownItem>
                          )}
                          {order?.testType !== 'HST' && (
                            <StyledDropdownItem
                              disabled={aobUploaded}
                              onClick={changeFileType('AOB', file.key)}>
                              AOB {aobUploaded && <span className="text-muted">(Uploaded)</span>}{' '}
                            </StyledDropdownItem>
                          )}
                          <StyledDropdownItem onClick={changeFileType('BILLING_DOC', file.key)}>
                            Billing Doc
                          </StyledDropdownItem>
                          <StyledDropdownItem onClick={changeFileType('OTHER', file.key)}>
                            Other
                          </StyledDropdownItem>
                          <Dropdown.Divider />
                          <Button
                            label="Permanently Delete"
                            className="dropdown-item"
                            href="#"
                            onClick={() => removeFile(file?.key)}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  )}
                </ListGroupItem>
              </TooltipWrapper>
            </div>
          </div>
        </div>
        <DocumentWrapper
          css={{ cursor: 'default', paddingLeft: zoom[idx] ? (zoom[idx] - 1) * 1000 : 0 }}
          {...(!isExpanded && { css: { visibility: 'hidden', height: 0 } })}
          onClick={(e) => e.stopPropagation()}>
          {isExpanded && !docUrl && (
            <div css={{ padding: 16, color: '#888', fontStyle: 'italic' }}>
              This document isn’t available.
            </div>
          )}
          {isExpanded && docUrl && ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext?.toLowerCase()) && (
            <DocImage src={docUrl} />
          )}
          {isExpanded && docUrl && ext?.toLowerCase() === 'pdf' && (
            <Document
              file={docUrl}
              onLoadSuccess={onDocumentLoadSuccess(idx)}
              externalLinkTarget="_blank">
              {Array.from(
                { length: Math.min(numPages[idx] ?? 0, PREVIEW_MAX_PAGES) },
                (_, i) => i + 1,
              ).map((page) => (
                <Page
                  pageNumber={page}
                  scale={zoom[idx] ?? 1}
                  key={page}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  height={Math.max(height * 0.65, 700)}
                />
              ))}
              {(numPages[idx] ?? 0) > PREVIEW_MAX_PAGES && (
                <div
                  css={{
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#555',
                    fontSize: '0.9rem',
                    background: '#fff',
                    borderTop: '1px solid #ddd',
                  }}>
                  Showing {PREVIEW_MAX_PAGES} of {numPages[idx]} pages.{' '}
                  <a href={docUrl} target="_blank" rel="noreferrer" onClick={trackDocumentDownload(file.key)}>
                    Download the file
                  </a>{' '}
                  to view the full document.
                </div>
              )}
            </Document>
          )}
          {isExpanded && docUrl && isMp3 && (
            <AudioPlayerWrapper>
              <AudioFileName>
                <i className="fas fa-music" />
                {file?.filename}
              </AudioFileName>
              <audio controls preload="metadata">
                <source src={docUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </AudioPlayerWrapper>
          )}
        </DocumentWrapper>
      </div>
    )
  })
}
